// FILE: frontend/src/pages/AnimatedQuiz.tsx
import * as React from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

import { API_BASE_URL } from "@/config";
import { auth, db } from "@/lib/firebase";
import { VOICE_OPTIONS } from "@/constants/voices";
import { VoiceSelector } from "@/components/VoiceSelector";  // ✅ ADD THIS LINE

type VideoFmt = "16:9" | "9:16" | "1:1";

// ============================================================
// TYPES & INTERFACES
// ============================================================

interface StyleOption {
    id: string;
    label: string;
    category?: "medical" | "generic";
}

interface UserQuota {
    user_id: string;
    plan: "free" | "starter" | "pro";
    active: boolean;
    videos: {
        used: number;
        limit: number;
        remaining: number;
    };
    longform_credits: number;
    features: {
        formats: string[];
        watermark: string;
        backgrounds: string;
        render_speed: string;
        hd_export: boolean;
        commercial_license: boolean;
        can_edit: boolean;
    };
}

type GeneratorMode = "mcq" | "flashcards" | "quiz_reveal" | "notes";

type FlashcardQA = {
    question: string;
    answer: string;
    explanation?: string;
};

type McqDraft = {
    question: string;
    options: string[]; // 2-4 (backend truncates >4)
    answer: string; // letter A-D or exact option text
    explanation?: string;
};

type ParseNotesResult = {
    items: FlashcardQA[];
    warnings: string[];
};

// ============================================================
// CONSTANTS
// ============================================================

const MAX_Q_PER_VIDEO = 10;

const STYLES: StyleOption[] = [
    { id: "med_physiology", label: "🫀 Physiology", category: "medical" },
    { id: "med_pharmacology", label: "💊 Pharmacology", category: "medical" },
    { id: "med_pathology", label: "🔬 Pathology", category: "medical" },
    { id: "med_microbiology", label: "🦠 Microbiology", category: "medical" },
    { id: "med_biochemistry", label: "🧬 Biochemistry", category: "medical" },
    { id: "med_biostatistics", label: "📊 Biostatistics", category: "medical" },
    { id: "med_public_health", label: "🏥 Public Health", category: "medical" },

    { id: "neon_waves", label: "🌊 Neon Waves", category: "generic" },
    { id: "particles_soft", label: "✨ Particles", category: "generic" },
    { id: "gradient_pan", label: "🌈 Gradient Pan", category: "generic" },
];



// ============================================================
// HELPER COMPONENTS
// ============================================================

function StylePreview({ style }: { style: string }) {
    const getBgClass = () => {
        if (style.includes("physiology")) return "bg-gradient-to-br from-[#1e3a8a] to-[#3b82f6]";
        if (style.includes("pharmacology")) return "bg-gradient-to-br from-[#581c87] to-[#a855f7]";
        if (style.includes("pathology")) return "bg-gradient-to-br from-[#7f1d1d] to-[#dc2626]";
        if (style.includes("microbiology")) return "bg-gradient-to-br from-[#064e3b] to-[#059669]";
        if (style.includes("biochemistry")) return "bg-gradient-to-br from-[#134e4a] to-[#14b8a6]";
        if (style.includes("biostatistics")) return "bg-gradient-to-br from-[#065f46] to-[#10b981]";
        if (style.includes("public_health")) return "bg-gradient-to-br from-[#854d0e] to-[#f59e0b]";
        return "bg-gradient-to-br from-gray-800 to-gray-900";
    };

    const styleName = STYLES.find((s) => s.id === style)?.label || style;

    return (
        <div className="card p-3 border-2 border-blue-200 dark:border-blue-800">
            <div className="text-xs font-medium mb-2 text-gray-600 dark:text-gray-400">Preview: {styleName}</div>
            <div className={`${getBgClass()} rounded-lg h-32 flex items-center justify-center text-white relative overflow-hidden`}>
                <div className="absolute inset-0 opacity-20">
                    <div
                        className="w-full h-full"
                        style={{
                            backgroundImage:
                                "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)",
                        }}
                    />
                </div>
                <div className="relative z-10 text-center p-4">
                    <div className="text-sm font-bold mb-1">Q: Sample Question</div>
                    <div className="text-xs opacity-80">A: Sample Answer</div>
                </div>
            </div>
        </div>
    );
}

// ============================================================
// HELPERS
// ============================================================

async function extractError(r: Response): Promise<string> {
    const ct = r.headers.get("content-type") || "";
    try {
        if (ct.includes("application/json")) {
            const j: any = await r.json();
            const d = j?.detail;
            if (typeof d === "string") return d;
            if (d && typeof d === "object") return JSON.stringify(d, null, 2);
            if (typeof j?.error === "string") return j.error;
            if (typeof j?.message === "string") return j.message;
            return JSON.stringify(j, null, 2);
        }
        const txt = await r.text();
        return txt || `${r.status} ${r.statusText}`;
    } catch {
        return `${r.status} ${r.statusText}`;
    }
}

function clampInt(n: number, min: number, max: number): number {
    if (Number.isNaN(n)) return min;
    return Math.max(min, Math.min(max, n));
}

function clampNum(n: number, min: number, max: number): number {
    if (!Number.isFinite(n)) return min;
    return Math.max(min, Math.min(max, n));
}


function normalizeFlashcardsLength(cards: FlashcardQA[], targetLen: number): FlashcardQA[] {
    const safeTarget = clampInt(targetLen, 1, MAX_Q_PER_VIDEO);
    if (cards.length === safeTarget) return cards;
    if (cards.length > safeTarget) return cards.slice(0, safeTarget);

    const next = [...cards];
    while (next.length < safeTarget) next.push({ question: "", answer: "", explanation: "" });
    return next;
}

function normalizeMcqLength(items: McqDraft[], targetLen: number): McqDraft[] {
    const safeTarget = clampInt(targetLen, 1, MAX_Q_PER_VIDEO);
    if (items.length === safeTarget) return items;
    if (items.length > safeTarget) return items.slice(0, safeTarget);

    const next = [...items];
    while (next.length < safeTarget)
        next.push({ question: "", options: ["", "", "", ""], answer: "", explanation: "" });
    return next;
}

function isCompleteFlashcard(c: FlashcardQA): boolean {
    return Boolean(c.question.trim()) && Boolean(c.answer.trim());
}

function stripEmptyOptions(options: string[]): string[] {
    return (options || []).map((o) => o.trim()).filter(Boolean);
}

function isValidMcqDraft(q: McqDraft): boolean {
    if (!q.question.trim()) return false;

    const opts = stripEmptyOptions(q.options);
    if (opts.length < 2) return false;
    if (opts.length > 4) return false;

    const ans = q.answer.trim();
    if (!ans) return false;

    const upper = ans.toUpperCase();
    if (["A", "B", "C", "D"].includes(upper)) {
        const idx = ["A", "B", "C", "D"].indexOf(upper);
        return idx >= 0 && idx < opts.length;
    }

    return opts.includes(ans);
}

function estimateVideoSeconds(params: { numQ: number; secondsPerQ: number; mode: GeneratorMode }): number {
    const q = clampInt(params.numQ, 1, MAX_Q_PER_VIDEO);
    const per = clampNum(params.secondsPerQ, 1, 60);
    const base = q * per;
    const buffer = params.mode === "mcq" ? 4 : 3;
    return clampNum(base + buffer, 2, 60);
}

async function generateAnimatedBackgroundUrl(args: {
    token?: string;
    style: string;
    video_format: VideoFmt;
    duration: number;
    fps: number;
    style_hue: number;
    style_sat: number;
    style_bri: number;
}): Promise<string> {
    const { token, ...payload } = args;

    const r = await fetch(`${API_BASE_URL}/api/video/background/animated`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
    });

    if (!r.ok) throw new Error(await extractError(r));

    const j: any = await r.json();
    if (!j?.background_url) throw new Error("No background_url returned");
    return String(j.background_url);
}

function getDefaultBgUrl(fmt: VideoFmt): string { const file = fmt === "9:16" ? "DNA_Helix_Motion-vertical.mp4" : fmt === "1:1" ? "DNA_Helix_Motion-Square.mp4" : "DNA_Helix_Motion-Landscape.mp4"; return `${API_BASE_URL}/static/templates/${file}`; }

function getStoredBgUrl(fmt: VideoFmt): string | null {
    try {
        const v = localStorage.getItem("selected_bg");
        const savedFormat = localStorage.getItem("selected_bg_format");
        if (!v) return getDefaultBgUrl(fmt);
        if (savedFormat && savedFormat !== fmt) return getDefaultBgUrl(fmt);
        const clean = v.split("?")[0] || v;
        if (!clean.startsWith("http") && !clean.startsWith("/")) return getDefaultBgUrl(fmt);
        if (!savedFormat && clean.includes("/static/templates/")) {
            const name = clean.toLowerCase();
            if (fmt === "9:16" && !name.includes("vertical")) return getDefaultBgUrl(fmt);
            if (fmt === "16:9" && !name.includes("landscape")) return getDefaultBgUrl(fmt);
            if (fmt === "1:1" && !name.includes("square")) return getDefaultBgUrl(fmt);
        }
        return clean;
    } catch {
        return null;
    }
}

function getWatermarkOverridesForPlan(_quota: UserQuota | null): { watermark_on?: boolean; watermark_text?: string } {
    return { watermark_on: true, watermark_text: "Made with AutoQuiz AI" };
}

function canCustomizeWatermark(_quota: UserQuota | null): boolean {
    return true;
}

function canDisableWatermark(_quota: UserQuota | null): boolean {
    return true;
}

function parseNotesImport(text: string, maxItems: number): ParseNotesResult {
    const warnings: string[] = [];
    const cleaned = (text || "").replace(/\r\n/g, "\n").trim();
    if (!cleaned) return { items: [], warnings: ["Paste some notes first."] };

    const blocks = cleaned
        .split(/\n{2,}/g)
        .map((b) => b.trim())
        .filter(Boolean);

    const out: FlashcardQA[] = [];

    const normalize = (s: string) => s.replace(/\s+/g, " ").trim();

    const tryParseQA = (block: string): FlashcardQA | null => {
        const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
        const joined = block;

        // Pattern 1: Q:/A:/E: (or Question/Answer/Explanation)
        const qMatch =
            joined.match(/(?:^|\n)\s*(?:Q|Question)\s*:\s*([\s\S]*?)(?=(?:\n\s*(?:A|Answer)\s*:|\n\s*(?:E|Explanation)\s*:|$))/i);
        const aMatch =
            joined.match(/(?:^|\n)\s*(?:A|Answer)\s*:\s*([\s\S]*?)(?=(?:\n\s*(?:E|Explanation)\s*:|$))/i);
        const eMatch =
            joined.match(/(?:^|\n)\s*(?:E|Explanation)\s*:\s*([\s\S]*?)\s*$/i);

        if (qMatch || aMatch || eMatch) {
            const q = normalize(qMatch?.[1] ?? "");
            const a = normalize(aMatch?.[1] ?? "");
            const e = normalize(eMatch?.[1] ?? "");
            if (q && a) return { question: q, answer: a, explanation: e };
            return null;
        }

        // Pattern 2: "Question? - Answer. Explanation..."
        // Split on " - " or " — "
        const dashIdx = joined.indexOf(" - ");
        const emDashIdx = joined.indexOf(" — ");
        const splitIdx = dashIdx >= 0 ? dashIdx : emDashIdx;

        if (splitIdx >= 0) {
            const left = normalize(joined.slice(0, splitIdx));
            const right = normalize(joined.slice(splitIdx + 3));
            if (left && right) {
                // Heuristic: first sentence as answer, rest as explanation
                const m = right.match(/^(.+?[.!?])\s+([\s\S]+)$/);
                if (m && m[1] && m[2]) {
                    return { question: left, answer: normalize(m[1]), explanation: normalize(m[2]) };
                }
                return { question: left, answer: right, explanation: "" };
            }
        }

        // Pattern 3: line-based: first line Q, second line A, rest E
        if (lines.length >= 2) {
            const q = normalize(lines[0] || "");
            const a = normalize(lines[1] || "");
            const e = normalize(lines.slice(2).join(" ") || "");
            if (q && a) return { question: q, answer: a, explanation: e };
        }

        return null;
    };

    for (const b of blocks) {
        if (out.length >= maxItems) break;
        const parsed = tryParseQA(b);
        if (!parsed) {
            warnings.push(`Could not parse block: "${normalize(b).slice(0, 80)}${b.length > 80 ? "…" : ""}"`);
            continue;
        }
        out.push(parsed);
    }

    if (out.length === 0 && warnings.length === 0) warnings.push("No items were parsed.");
    return { items: out, warnings };
}

// ============================================================
// MAIN
// ============================================================

export default function AnimatedQuiz() {
    const navigate = useNavigate();

    React.useEffect(() => {
        const stored = localStorage.getItem("selected_bg");
        if (!stored || stored.includes("synth_bg_720x1280.mp4")) {
            localStorage.setItem("selected_bg", getDefaultBgUrl(fmt));
        }
    }, []);

    // State
    const [quota, setQuota] = React.useState<UserQuota | null>(null);
    const [quotaLoading, setQuotaLoading] = React.useState(false);
    const [userEmail, setUserEmail] = React.useState<string>("");

    const [topic, setTopic] = React.useState("cell biology");
    const [useAnimated, setUseAnimated] = React.useState(true);
    const [fmt, setFmt] = React.useState<VideoFmt>("16:9");
    const style = "";

    // Optional color mood knobs
    const [hue, _setHue] = React.useState(210);
    const [sat, _setSat] = React.useState(1.3);
    const [bri, _setBri] = React.useState(0.12);

    // Voice/duration/questions
    const [voice, setVoice] = React.useState(VOICE_OPTIONS[0]?.id ?? "");
    const [numQ, setNumQ] = React.useState(3);
    const [dur, setDur] = React.useState(6);

    // Mode
    const [mode, setMode] = React.useState<GeneratorMode>("flashcards");

    // Watermark customization
    const [watermarkEnabled, setWatermarkEnabled] = React.useState(true);
    const [customWatermarkImage, setCustomWatermarkImage] = React.useState<string>("");
    const [watermarkText, setWatermarkText] = React.useState<string>("AutoQuiz AI");
    const [watermarkWidth, setWatermarkWidth] = React.useState<number>(200);
    const [watermarkOpacity, setWatermarkOpacity] = React.useState<number>(0.9);

    // Hook/CTA customization
    const [hookText, setHookText] = React.useState<string>("");
    const [hookVisible, setHookVisible] = React.useState(true); // Show text on screen
    const [hookColor, setHookColor] = React.useState<string>("#FFFFFF"); // white
    
    const [ctaText, setCtaText] = React.useState<string>("");
    const [ctaVisible, setCtaVisible] = React.useState(true); // Show text on screen
    const [ctaColor, setCtaColor] = React.useState<string>("#00FF00"); // lime

    // Custom input modes
    const [useCustomFlashcards, setUseCustomFlashcards] = React.useState(false);
    const [flashcards, setFlashcards] = React.useState<FlashcardQA[]>([
        { question: "", answer: "", explanation: "" },
        { question: "", answer: "", explanation: "" },
        { question: "", answer: "", explanation: "" },
    ]);

    const [useCustomMcq, setUseCustomMcq] = React.useState(false);
    const [mcqs, setMcqs] = React.useState<McqDraft[]>([
        { question: "", options: ["", "", "", ""], answer: "", explanation: "" },
        { question: "", options: ["", "", "", ""], answer: "", explanation: "" },
        { question: "", options: ["", "", "", ""], answer: "", explanation: "" },
    ]);

    // Notes import editor
    const [notesImport, setNotesImport] = React.useState<string>("");
    const [notesWarnings, setNotesWarnings] = React.useState<string[]>([]);

    const [generating, setGenerating] = React.useState(false);

    // ============================================================
    // COMPUTED VALUES
    // ============================================================



    const showFlashcardEditor = (mode === "flashcards" || mode === "notes") && useCustomFlashcards;
    const showMcqEditor = mode === "mcq" && useCustomMcq;

    const completedFlashcardsCount = React.useMemo(() => {
        return flashcards.filter(isCompleteFlashcard).length;
    }, [flashcards]);

    const notesForcedNumQ = React.useMemo(() => {
        if (mode !== "notes") return null;
        const n = clampInt(completedFlashcardsCount, 1, MAX_Q_PER_VIDEO);
        return n;
    }, [mode, completedFlashcardsCount]);

    // ============================================================
    // EFFECTS
    // ============================================================

    React.useEffect(() => {
        const stored = localStorage.getItem("user_email");
        if (stored) {
            setUserEmail(stored);
            loadQuota(stored);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        setNumQ((prev) => clampInt(prev, 1, MAX_Q_PER_VIDEO));
    }, []);

    React.useEffect(() => {
        if (useCustomFlashcards) setFlashcards((prev) => normalizeFlashcardsLength(prev, numQ));
        if (useCustomMcq) setMcqs((prev) => normalizeMcqLength(prev, numQ));
    }, [numQ, useCustomFlashcards, useCustomMcq]);

    // ✅ Notes mode: disable animated backgrounds and default to custom items editor
    React.useEffect(() => {
        if (mode === "notes") {
            setUseAnimated(false);
            setUseCustomMcq(false);
            setUseCustomFlashcards(true);
        }
    }, [mode]);

    // ✅ Notes mode: force numQ to completed items count (ignores number input)
    React.useEffect(() => {
        if (mode !== "notes") return;
        const forced = clampInt(completedFlashcardsCount, 1, MAX_Q_PER_VIDEO);
        setNumQ(forced);
    }, [mode, completedFlashcardsCount]);

    React.useEffect(() => {
        function handleKeyPress(e: KeyboardEvent) {
            if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                if (!generating && topic.trim()) void handleGenerate();
            }
        }

        window.addEventListener("keydown", handleKeyPress);
        return () => window.removeEventListener("keydown", handleKeyPress);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        generating,
        topic,
        mode,
        numQ,
        dur,
        fmt,
        voice,
        style,
        useAnimated,
        hue,
        sat,
        bri,
        useCustomFlashcards,
        flashcards,
        useCustomMcq,
        mcqs,
        watermarkEnabled,
        customWatermarkImage,
        watermarkText,
        hookText,
        ctaText,
    ]);

    // ============================================================
    // QUOTA
    // ============================================================

    async function loadQuota(email: string) {
        if (!email) return;

        setQuotaLoading(true);
        try {
            const user = auth?.currentUser;
            const headers: HeadersInit = { "Content-Type": "application/json" };

            if (user) {
                try {
                    const token = await user.getIdToken();
                    headers["Authorization"] = `Bearer ${token}`;
                } catch (err) {
                    console.warn("Failed to get auth token:", err);
                    toast.error("Authentication failed. Please sign in again.");
                    return;
                }
            }

            const response = await fetch(`${API_BASE_URL}/api/payment/checkQuota?email=${encodeURIComponent(email)}`, {
                headers,
            });

            if (response.ok) {
                const data = await response.json();
                setQuota(data);
                console.log("✅ Quota loaded:", data);
            } else if (response.status === 401) {
                console.warn("⚠️ Not authenticated - quota check failed");
                toast.error("Please sign in to check your quota");
            } else if (response.status === 404) {
                console.warn("⚠️ User not found");
                toast.error("User not found. Please check your email.");
            } else {
                throw new Error(`Failed to load quota: ${response.status}`);
            }
        } catch (error) {
            console.error("Failed to load quota:", error);
            toast.error("Failed to load quota information");
        } finally {
            setQuotaLoading(false);
        }
    }

    function saveUserEmail(email: string) {
        localStorage.setItem("user_email", email);
        setUserEmail(email);
        void loadQuota(email);
    }

    async function saveToHistory(videoUrl: string, meta?: { mode?: GeneratorMode }) {
        if (!auth.currentUser?.uid) {
            console.warn("Cannot save to history: No user logged in");
            return;
        }

        try {
            const quizRef = collection(db, `users/${auth.currentUser.uid}/quizzes`);
            await addDoc(quizRef, {
                title: topic.slice(0, 100),
                video_url: videoUrl,
                createdAt: serverTimestamp(),
                topic,
                format: fmt,
                voice,
                style: useAnimated && mode !== "notes" ? style : null,
                numQuestions: numQ,
                generator_mode: meta?.mode ?? mode,
                notes_beta: mode === "notes",
            });
            console.log("✅ Video saved to history");
        } catch (error) {
            console.error("❌ Failed to save to history:", error);
        }
    }

    // ============================================================
    // EDITOR HELPERS
    // ============================================================

    function updateFlashcard(idx: number, patch: Partial<FlashcardQA>) {
        setFlashcards((prev) => {
            const next = [...prev];
            const current = next[idx] ?? { question: "", answer: "", explanation: "" };
            next[idx] = { ...current, ...patch };
            return next;
        });
    }

    function updateMcq(idx: number, patch: Partial<McqDraft>) {
        setMcqs((prev) => {
            const next = [...prev];
            const current = next[idx] ?? { question: "", options: ["", "", "", ""], answer: "", explanation: "" };
            next[idx] = { ...current, ...patch };
            return next;
        });
    }

    function updateMcqOption(qIdx: number, optIdx: number, value: string) {
        setMcqs((prev) => {
            const next = [...prev];
            const cur = next[qIdx] ?? { question: "", options: ["", "", "", ""], answer: "", explanation: "" };
            const options = [...(cur.options ?? ["", "", "", ""])];
            options[optIdx] = value;
            next[qIdx] = { ...cur, options };
            return next;
        });
    }

    function handleParseNotesImport() {
        const result = parseNotesImport(notesImport, MAX_Q_PER_VIDEO);
        setNotesWarnings(result.warnings);

        if (result.items.length === 0) {
            toast.error("No notes items parsed. Check formatting.");
            return;
        }

        // Fill flashcards array with parsed items, pad to max for editing comfort
        const filled = normalizeFlashcardsLength(result.items, clampInt(result.items.length, 1, MAX_Q_PER_VIDEO));
        setFlashcards(filled);
        setUseCustomFlashcards(true);

        toast.success(`Imported ${result.items.length} item(s).`);
    }

    async function handleWatermarkUpload(file: File) {
        if (!file) return;

        try {
            const user = auth?.currentUser;

            // ✅ Check if user is logged in
            if (!user) {
                toast.error("Please log in to upload a custom watermark");
                return;
            }

            const token = await user.getIdToken();

            const formData = new FormData();
            formData.append("file", file);

            const response = await fetch(`${API_BASE_URL}/api/video/upload-background`, {
                method: "POST",
                headers: {
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: formData,
            });

            if (!response.ok) {
                const errorMsg = await extractError(response);
                // ✅ Better error message for 401
                if (response.status === 401) {
                    throw new Error("Authentication failed. Please log out and log back in.");
                }
                throw new Error(errorMsg);
            }

            const result = await response.json();
            const uploadedUrl = result.url || result.background_url;

            if (!uploadedUrl) throw new Error("No URL returned from upload");

            // Extract the path after /static/
            const staticPath = uploadedUrl.includes("/static/")
                ? uploadedUrl.substring(uploadedUrl.indexOf("/static/"))
                : uploadedUrl;

            setCustomWatermarkImage(staticPath);
            toast.success("Watermark logo uploaded successfully!");
        } catch (error: any) {
            console.error("Watermark upload failed:", error);
            toast.error(error?.message || "Failed to upload watermark logo");
        }
    }
    // ============================================================
    // GENERATE
    // ============================================================

    async function handleGenerate() {
        if (!topic.trim()) {
            toast.error("Enter a topic");
            return;
        }        if ((mode === "flashcards" || mode === "notes") && useCustomFlashcards) {
            const completed = flashcards.filter(isCompleteFlashcard);
            if (completed.length === 0) {
                toast.error("Add at least 1 complete item (question + answer).");
                return;
            }
        }

        if (mode === "mcq" && useCustomMcq) {
            const completed = mcqs.filter(isValidMcqDraft);
            if (completed.length === 0) {
                toast.error("Add at least 1 valid MCQ (question, 2-4 options, answer).");
                return;
            }
        }

        setGenerating(true);

        try {
            const token = await auth.currentUser?.getIdToken(true).catch(() => undefined);

            const payload: any = {
                topic: topic.trim(),
                num_questions: clampInt(numQ, 1, MAX_Q_PER_VIDEO),
                duration: dur,
                voice,
                video_format: fmt,
                watermark: watermarkEnabled,
                overrides: {},
            };

            // ✅ Watermark settings based on user preferences and plan
            if (watermarkEnabled) {
                const planDefaults = getWatermarkOverridesForPlan(quota);

                payload.overrides.watermark_on = true;

                // Custom watermark image if provided
                if (customWatermarkImage.trim()) {
                    payload.overrides.watermark_image = customWatermarkImage.trim();
                }

                // Custom watermark text or plan default
                if (watermarkText.trim()) {
                    payload.overrides.watermark_text = watermarkText.trim();
                } else if (planDefaults.watermark_text) {
                    payload.overrides.watermark_text = planDefaults.watermark_text;
                }

                // Watermark customization
                payload.overrides.watermark_w = watermarkWidth;
                payload.overrides.watermark_opacity = watermarkOpacity;
            } else {
                // User disabled watermark
                payload.overrides.watermark_on = false;
                payload.overrides.watermark_text = "";
            }

            // ✅ Hook - always send if text exists (visibility controlled separately)
            if (hookText.trim()) {
                payload.hook = hookText.trim();
                payload.overrides.hook_visible = hookVisible; // Control text display
                if (hookColor !== "#FFFFFF") {
                    payload.overrides.hook_color = hookColor;
                }
            }
            // ✅ CTA - always send if text exists (visibility controlled separately)
            if (ctaText.trim()) {
                payload.cta = ctaText.trim();
                payload.overrides.cta_text = ctaText.trim();
                payload.overrides.cta_visible = ctaVisible; // Control text display
                if (ctaColor !== "#00FF00") {
                    payload.overrides.cta_color = ctaColor;
                }
            }

            if (mode === "notes") {
                // ✅ Set render_mode at top level (backend checks this)
                payload.render_mode = "notes_mvp";
                payload.output_mode = "full";
                payload.tiktok_friendly = false;

                // ✅ Remove conflicting override (backend will read top-level render_mode)
                delete payload.overrides.render_mode;

                if (useCustomFlashcards) {
                    const completed = flashcards
                        .filter(isCompleteFlashcard)
                        .slice(0, MAX_Q_PER_VIDEO)
                        .map((c) => ({
                            question: c.question.trim(),
                            answer: c.answer.trim(),
                            explanation: (c.explanation ?? "").trim(),
                        }));

                    if (completed.length === 0) {
                        toast.error("No completed items to generate. Add at least one question and answer.");
                        setGenerating(false);
                        return;
                    }

                    // ✅ Backend now accepts both 'questions' and 'items' for notes mode
                    payload.questions = completed;
                    payload.num_questions = completed.length;

                    console.log(`📝 Notes mode: Sending ${completed.length} items to backend`);
                } else {
                    // User wants AI to generate from topic
                    console.log(`📝 Notes mode: Backend will generate from topic "${topic}"`);
                }
            } else if (mode === "quiz_reveal") {
                // Dedicated TikTok-style Q&A mode.
                // Backend guarantees exactly 3 choices and forces portrait.
                payload.render_mode = "quiz_reveal";
                payload.video_format = "9:16";
                payload.output_mode = "full";
                payload.explanation_mode = "answer_only";
                payload.tiktok_friendly = false;

                payload.overrides = {
                    ...payload.overrides,
                    quiz_reveal: true,
                    countdown_bar_on: true,
                    clock_mode: "digital",
                };

            } else if (mode === "flashcards") {
                payload.render_mode = "qa_only";
                payload.output_mode = "full";
                payload.tiktok_friendly = false;

                if (useCustomFlashcards) {
                    const completed = flashcards
                        .filter(isCompleteFlashcard)
                        .slice(0, MAX_Q_PER_VIDEO)
                        .map((c) => ({
                            question: c.question.trim(),
                            answer: c.answer.trim(),
                            explanation: (c.explanation ?? "").trim(),
                        }));

                    payload.questions = completed;
                    payload.num_questions = completed.length;
                }
            } else {
                payload.render_mode = "mcq";
                payload.output_mode = fmt === "9:16" ? "tiktok" : "full";
                payload.tiktok_friendly = fmt === "9:16";

                if (useCustomMcq) {
                    const completed = mcqs
                        .filter(isValidMcqDraft)
                        .slice(0, MAX_Q_PER_VIDEO)
                        .map((q) => {
                            const options = stripEmptyOptions(q.options).slice(0, 4);
                            return {
                                question: q.question.trim(),
                                options,
                                answer: q.answer.trim(),
                                explanation: (q.explanation ?? "").trim(),
                            };
                        });

                    payload.questions = completed;
                    payload.num_questions = completed.length;
                }
            }

            // ✅ Animated background: skip for notes mode
            if (useAnimated && mode !== "notes") {
                let bgUrl = getStoredBgUrl(fmt);

                if (!bgUrl) {
                    const bgDuration = estimateVideoSeconds({
                        numQ: payload.num_questions,
                        secondsPerQ: dur,
                        mode,
                    });

                    bgUrl = await generateAnimatedBackgroundUrl({
                        token,
                        style,
                        video_format: fmt,
                        duration: bgDuration,
                        fps: 30,
                        style_hue: hue,
                        style_sat: sat,
                        style_bri: bri,
                    });

                    try {
                        localStorage.setItem("selected_bg", bgUrl);
                        window.dispatchEvent(new StorageEvent("storage", { key: "selected_bg", newValue: bgUrl }));
                    } catch {
                        // ignore
                    }
                }

                payload.background = bgUrl;
                payload.background_url = bgUrl;
                payload.animated_background = true;
                payload.style = style;
                payload.bg_fps = 30;
                payload.style_hue = hue;
                payload.style_sat = sat;
                payload.style_bri = bri;
            }

            console.log("📤 Sending payload:", JSON.stringify(payload, null, 2));

            const response = await fetch(`${API_BASE_URL}/api/video/generate-video`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                let errorMessage = `Server error: ${response.status}`;
                try {
                    const errorText = await response.text();
                    console.error("❌ Error response:", errorText);
                    try {
                        const errorJson = JSON.parse(errorText);
                        errorMessage = errorJson.detail || errorJson.message || errorText;
                    } catch {
                        errorMessage = errorText || errorMessage;
                    }
                } catch (e) {
                    console.error("Failed to read error response:", e);
                }
                throw new Error(errorMessage);
            }

            const result = await response.json();
            console.log("✅ Generation successful:", result);

            const videoUrl = result.video_url || result.url || result.videoUrl;
            if (!videoUrl) throw new Error("No video URL returned from server");

            const successLabel = mode === "notes" ? "Notes" : mode === "flashcards" ? "Flashcard" : "MCQ";
            toast.success(`${successLabel} video generated successfully!`);

            await saveToHistory(videoUrl, { mode });
            navigate("/video", { state: { url: videoUrl } });
        } catch (error: any) {
            console.error("❌ Generation failed:", error);
            toast.error(error?.message || "Failed to generate video");
        } finally {
            setGenerating(false);
        }
    }

    // ============================================================
    // RENDER
    // ============================================================

    return (
        <div className="p-6 max-w-4xl mx-auto space-y-6">
            <div>
                <h1 className="text-2xl font-bold">Animated Quiz Generator</h1>
                <p className="text-gray-600 dark:text-gray-300 mt-1">
                    Generate quiz videos with animated backgrounds, flashcards, and study notes.
                </p>
            </div>

            {quotaLoading && (
                <div className="card p-4 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800">
                    <div className="flex items-center gap-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                        <span className="text-sm text-blue-600 dark:text-blue-400">Loading credits...</span>
                    </div>
                </div>
            )}

            {quota && !quotaLoading && (
                <div className="card p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-blue-200 dark:border-blue-800">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="text-3xl">🎬</div>
                            <div>
                                <div className="font-bold text-lg">
                                    <span className="text-blue-600 dark:text-blue-400">{quota.videos.remaining} Credits</span>
                                </div>
                                <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                                    Final video generation uses 5 credits.
                                </div>
                                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                    Watermark:{" "}
                                    {watermarkEnabled
                                        ? `On (${watermarkText.trim() || "AutoQuiz AI"})`
                                        : "Off"}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {!userEmail && !quotaLoading && (
                <div className="card p-4 bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-500">
                    <div className="flex items-center gap-3">
                        <div className="flex-1">
                            <label className="block text-sm font-medium mb-2">Enter your email to load credits:</label>
                            <input
                                type="email"
                                placeholder="your@email.com"
                                className="w-full border rounded px-3 py-2"
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") saveUserEmail(e.currentTarget.value);
                                }}
                            />
                        </div>
                        <button
                            onClick={(e) => {
                                const input = (e.currentTarget.previousElementSibling as HTMLDivElement)?.querySelector("input");
                                if (input?.value) saveUserEmail(input.value);
                            }}
                            className="btn bg-blue-500 text-white hover:bg-blue-600 px-4 py-2 rounded"
                        >
                            Load Quota
                        </button>
                    </div>
                </div>
            )}

            {/* Main Form */}
            <div className="grid gap-4 sm:grid-cols-2">
                {/* Mode Selection */}
                <label className="space-y-2 sm:col-span-2">
                    <span className="text-sm font-semibold flex items-center gap-2">
                        <span className="text-lg">🎯</span>
                        Generator Mode
                    </span>
                    <select
                        value={mode}
                        onChange={(e) => {
                            const newMode = e.target.value as GeneratorMode;
                            setMode(newMode);

                            if (newMode === "flashcards") setUseCustomMcq(false);
                            if (newMode === "mcq") setUseCustomFlashcards(false);
                            if (newMode === "notes") setUseCustomMcq(false);

                            if (newMode === "quiz_reveal") {
                                setUseCustomMcq(false);
                                setUseCustomFlashcards(false);
                                setFmt("9:16");
                            }
                        }}
                        className="w-full border-2 p-3 rounded-lg font-medium text-base"
                    >
                        <option value="flashcards">🎴 Q&A Reveal — Classic</option>
                        <option value="quiz_reveal">📝 Quiz Reveal — 3 choices with countdown and answer reveal</option>
                        <option value="mcq">📝 Multiple Choice - Quiz with A/B/C/D options and explanations</option>
                        <option value="notes">🧾 Notes MVP - Study notes with highlight & progressive reveal ⭐ NEW</option>
                    </select>
                </label>

                {/* Notes mode info + import */}
                {mode === "notes" && (
                    <div className="sm:col-span-2 card p-4 border-2 border-green-200 dark:border-green-700 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 space-y-3">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="font-semibold text-green-800 dark:text-green-200 flex items-center gap-2">
                                    🧾 Notes MVP Mode ⭐
                                    <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-bold">NEW</span>
                                </div>
                                <div className="text-sm text-green-700 dark:text-green-300 mt-1">
                                    Creates a clean PDF-style notes page with questions that highlight during narration
                                    and answers that progressively reveal. Perfect for study reviews!
                                </div>
                                <div className="text-xs text-green-600 dark:text-green-400 mt-2">
                                    ℹ️ Animated backgrounds are disabled in notes mode for better readability.
                                </div>
                            </div>
                            <button
                                className="btn px-3 py-2 rounded bg-gray-200 dark:bg-gray-700 text-sm shrink-0"
                                onClick={() => {
                                    setNotesImport("");
                                    setNotesWarnings([]);
                                }}
                                type="button"
                            >
                                Clear Import
                            </button>
                        </div>

                        <div className="space-y-3">
                            <div className="text-sm font-medium text-green-800 dark:text-green-200">📝 Import Notes (Optional)</div>
                            <div className="text-xs text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-800/30 p-2 rounded">
                                <strong>Tip:</strong> Paste your existing notes below and click "Parse & Fill Editor" to automatically
                                format them, or manually add items in the editor that appears below.
                            </div>
                            <textarea
                                value={notesImport}
                                onChange={(e) => setNotesImport(e.target.value)}
                                className="w-full border-2 border-green-200 dark:border-green-700 p-3 rounded-lg min-h-[140px] font-mono text-sm"
                                placeholder={`📋 Supported formats:

1) Structured format:
Q: What is the first priority in DKA management?
A: Fluid resuscitation with normal saline
E: Addressing hypovolemia is critical before insulin therapy

2) Simple format:
What is DKA? - Diabetic ketoacidosis. A serious complication of diabetes.

3) Line format:
Question line here
Answer line here
Optional explanation lines...`}
                            />
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={handleParseNotesImport}
                                    className="btn bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-semibold"
                                >
                                    📥 Parse & Fill Editor
                                </button>
                                <div className="text-sm text-green-600 dark:text-green-400">
                                    <strong>{completedFlashcardsCount}</strong> complete items / {MAX_Q_PER_VIDEO} max
                                </div>
                            </div>

                            {notesWarnings.length > 0 && (
                                <div className="text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-800 rounded-lg p-3">
                                    <div className="font-semibold mb-2 flex items-center gap-2">
                                        ⚠️ Import Warnings:
                                    </div>
                                    <ul className="list-disc ml-5 space-y-1 text-xs">
                                        {notesWarnings.slice(0, 6).map((w, i) => (
                                            <li key={i}>{w}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Custom Toggles */}
                {(mode === "flashcards" || mode === "notes") && (
                    <label className="flex items-center gap-3 p-3 rounded-md border dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 sm:col-span-2">
                        <input
                            type="checkbox"
                            checked={useCustomFlashcards}
                            onChange={(e) => setUseCustomFlashcards(e.target.checked)}
                            className="w-4 h-4"
                        />
                        <div className="flex-1">
                            <div className="font-medium">{mode === "notes" ? "Use custom notes items" : "Use custom Q&A items"}</div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                                Sends <code>{"{question, answer, explanation?}"}</code> to the video generator.
                            </div>
                        </div>
                    </label>
                )}

                {mode === "mcq" && (
                    <label className="flex items-center gap-3 p-3 rounded-md border dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 sm:col-span-2">
                        <input
                            type="checkbox"
                            checked={useCustomMcq}
                            onChange={(e) => setUseCustomMcq(e.target.checked)}
                            className="w-4 h-4"
                        />
                        <div className="flex-1">
                            <div className="font-medium">Use custom MCQs</div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                                Sends <code>{"{question, options:[2-4], answer, explanation?}"}</code> to the video generator.
                            </div>
                        </div>
                    </label>
                )}

                {/* Use Animated Background Toggle */}
                <label
                    className={`flex items-center gap-3 p-3 rounded-md border dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${mode === "notes" ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                    title={mode === "notes" ? "Notes mode disables animated backgrounds for better readability." : ""}
                >
                    <input
                        type="checkbox"
                        checked={useAnimated}
                        onChange={(e) => {
                            if (mode === "notes") return;
                            setUseAnimated(e.target.checked);
                        }}
                        className="w-4 h-4"
                        disabled={mode === "notes"}
                    />
                    <span className="font-medium">Use animated background</span>
                    {mode === "notes" && <span className="text-xs text-gray-500">(Disabled in Notes mode)</span>}
                </label>

                {/* Watermark Customization Section */}
                {quota && canCustomizeWatermark(quota) && (
                    <details className="sm:col-span-2 card border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50 dark:border-purple-700 dark:from-purple-900/20 dark:to-pink-900/20">
                        <summary className="cursor-pointer p-4 font-semibold text-purple-800 dark:text-purple-200">
                            🎨 Watermark settings
                            <span className="ml-2 text-xs font-normal text-purple-600 dark:text-purple-400">
                                Optional
                            </span>
                        </summary>

                        <div className="space-y-4 px-4 pb-4">
                            <p className="text-sm text-purple-700 dark:text-purple-300">
                                Customize the watermark text, upload your own logo, or disable it.
                            </p>
                            {/* Watermark Toggle - Pro only */}
                            {canDisableWatermark(quota) && (
                                <label className="flex items-center gap-3 p-3 rounded-md border-2 border-purple-200 dark:border-purple-700 cursor-pointer hover:bg-purple-100 dark:hover:bg-purple-900/30">
                                    <input
                                        type="checkbox"
                                        checked={watermarkEnabled}
                                        onChange={(e) => setWatermarkEnabled(e.target.checked)}
                                        className="w-4 h-4"
                                    />
                                    <div className="flex-1">
                                        <div className="font-medium">Enable watermark</div>
                                        <div className="text-xs text-purple-600 dark:text-purple-400">
                                            You can completely disable the watermark
                                        </div>
                                    </div>
                                </label>
                            )}

                            {/* Watermark Settings - shown when watermark is enabled */}
                            {watermarkEnabled && (
                                <div className="space-y-4 p-4 bg-white dark:bg-gray-900/50 rounded-lg border-2 border-purple-200 dark:border-purple-700">
                                    {/* Logo Upload */}
                                    <div className="space-y-2">
                                        <label className="block">
                                            <span className="text-sm font-medium">🖼️ Custom Logo (optional)</span>
                                            <div className="flex gap-2 mt-1">
                                                <input
                                                    type="text"
                                                    value={customWatermarkImage}
                                                    onChange={(e) => setCustomWatermarkImage(e.target.value)}
                                                    placeholder="/static/assets/your-logo.png"
                                                    className="flex-1 border-2 border-purple-200 dark:border-purple-700 p-2 rounded"
                                                />
                                                <label className="btn border-2 border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/30 cursor-pointer px-4 py-2 rounded font-medium">
                                                    📤 Upload
                                                    <input
                                                        type="file"
                                                        accept="image/png,image/jpeg,image/webp"
                                                        className="hidden"
                                                        onChange={(e) => {
                                                            const file = e.target.files?.[0];
                                                            if (file) handleWatermarkUpload(file);
                                                            e.target.value = "";
                                                        }}
                                                    />
                                                </label>
                                            </div>
                                            <div className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                                                Upload a PNG, JPG, or WebP image. Saved to /static/assets/
                                            </div>
                                        </label>
                                    </div>

                                    {/* Watermark Text */}
                                    <div className="space-y-2">
                                        <label className="block">
                                            <span className="text-sm font-medium">📝 Watermark Text</span>
                                            <input
                                                type="text"
                                                value={watermarkText}
                                                onChange={(e) => setWatermarkText(e.target.value)}
                                                placeholder="AutoQuiz AI"
                                                className="w-full border-2 border-purple-200 dark:border-purple-700 p-2 rounded mt-1"
                                            />
                                            <div className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                                                Used when no logo image is provided
                                            </div>
                                        </label>
                                    </div>

                                    {/* Advanced Settings */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <label className="space-y-1">
                                            <span className="text-sm font-medium">📏 Logo Width (px)</span>
                                            <input
                                                type="number"
                                                min={80}
                                                max={640}
                                                value={watermarkWidth}
                                                onChange={(e) => setWatermarkWidth(clampInt(Number(e.target.value), 80, 640))}
                                                className="w-full border-2 border-purple-200 dark:border-purple-700 p-2 rounded"
                                            />
                                        </label>

                                        <label className="space-y-1">
                                            <span className="text-sm font-medium">💫 Opacity (0-1)</span>
                                            <input
                                                type="number"
                                                step={0.1}
                                                min={0}
                                                max={1}
                                                value={watermarkOpacity}
                                                onChange={(e) => setWatermarkOpacity(clampNum(Number(e.target.value), 0, 1))}
                                                className="w-full border-2 border-purple-200 dark:border-purple-700 p-2 rounded"
                                            />
                                        </label>
                                    </div>

                                    {/* Clear Logo Button */}
                                    {customWatermarkImage && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setCustomWatermarkImage("");
                                                toast.success("Custom watermark logo cleared");
                                            }}
                                            className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 underline"
                                        >
                                            🗑️ Clear custom logo
                                        </button>
                                    )}

                                    {/* Preview */}
                                    <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded border-2 border-purple-200 dark:border-purple-700">
                                        <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Preview:</div>
                                        <div className="flex items-center gap-2 p-2 bg-white dark:bg-gray-900 rounded">
                                            {customWatermarkImage ? (
                                                <div className="text-sm font-medium text-purple-600 dark:text-purple-400">
                                                    🖼️ Custom logo uploaded
                                                </div>
                                            ) : (
                                                <div className="text-sm text-gray-600 dark:text-gray-400" style={{ opacity: watermarkOpacity }}>
                                                    {watermarkText || "AutoQuiz AI"}
                                                </div>
                                            )}
                                            <div className="ml-auto text-xs text-gray-500">
                                                {watermarkWidth}px × {watermarkOpacity.toFixed(1)} opacity
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </details>
                )}

                {/* Video Format */}
                <label className="space-y-1">
                    <span className="text-sm font-medium">Video Format</span>
                    <select
                        value={fmt}
                        onChange={(e) => {
                            setFmt(e.target.value as VideoFmt);
                        }}
                        className="w-full border p-2 rounded"
                    >
                        <option value="9:16">📱 9:16 (Vertical/TikTok/Shorts)</option>
                        <option value="16:9">📺 16:9 (Horizontal/YouTube)</option>
                        <option value="1:1">⬜ 1:1 (Square/Instagram)</option>
                    </select>
                    <button
                        type="button"
                        onClick={() => navigate(`/backgrounds?format=${fmt}&return=/animated-quiz`)}
                        className="mt-2 w-full rounded-lg border border-blue-300 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-950/30"
                    >
                        🎞 Choose MP4 Background
                    </button>
                </label>


                {/* Voice */}
                <label className="space-y-1">
                    <span className="text-sm font-medium">🎙️ Voice</span>
                    <VoiceSelector
                        value={voice}
                        onChange={setVoice}
                    />
                </label>

                {/* Topic */}
                <label className="space-y-1 sm:col-span-2">
                    <span className="text-sm font-medium">📚 Topic</span>
                    <input
                        className="w-full border-2 p-3 rounded-lg"
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        placeholder="e.g., DKA management, cardiovascular physiology, pharmacology review"
                    />
                </label>

                {/* Hook & CTA Section - ENHANCED WITH TOGGLES AND COLOR PICKERS */}
<details className="sm:col-span-2 card border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-cyan-50 dark:border-blue-700 dark:from-blue-900/20 dark:to-cyan-900/20">
    <summary className="cursor-pointer p-4 font-semibold text-blue-800 dark:text-blue-200">
        🎬 Hook & Call-to-Action
        <span className="ml-2 text-xs font-normal text-blue-600 dark:text-blue-400">
            Optional
        </span>
    </summary>

    <div className="space-y-4 px-4 pb-4">
        <p className="text-sm text-blue-700 dark:text-blue-300">
            Add an intro hook or closing call-to-action when you need one.
        </p>
        {/* Hook Section */}
        <div className="p-3 bg-white dark:bg-gray-900/50 rounded-lg border-2 border-blue-200 dark:border-blue-700">
            {/* Hook Toggle */}
            <label className="flex items-center gap-3 mb-3 cursor-pointer">
                <input
                    type="checkbox"
                    checked={hookVisible}
                    onChange={(e) => setHookVisible(e.target.checked)}
                    className="w-4 h-4"
                />
                <div className="flex-1">
                    <div className="font-medium">📺 Show Hook Text on Screen</div>
                    <div className="text-xs text-blue-600 dark:text-blue-400">
                        Display hook text visually (TTS always reads it aloud)
                    </div>
                </div>
            </label>

            {/* Hook Inputs */}
            <div className="space-y-3">
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Hook Text</label>
                        <textarea
                            value={hookText}
                            onChange={(e) => setHookText(e.target.value)}
                            placeholder="Ready to test your knowledge?&#10;Let's dive in!"
                            className="w-full border-2 border-blue-200 dark:border-blue-700 p-3 rounded-lg min-h-[80px] font-mono text-sm"
                            maxLength={200}
                        />
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-blue-600 dark:text-blue-400">
                                💡 Press Enter for new lines
                            </span>
                            <span className={`${hookText.length > 180 ? 'text-orange-600' : 'text-gray-500'}`}>
                                {hookText.length}/200
                            </span>
                        </div>
                    </div>

                    {/* Hook Color Picker */}
                    <div className="grid grid-cols-2 gap-3">
                        <label className="space-y-2">
                            <span className="text-sm font-medium flex items-center gap-2">
                                🎨 Text Color
                            </span>
                            <div className="flex gap-2">
                                <input
                                    type="color"
                                    value={hookColor}
                                    onChange={(e) => setHookColor(e.target.value)}
                                    className="w-12 h-10 rounded border-2 border-blue-200 dark:border-blue-700 cursor-pointer"
                                />
                                <input
                                    type="text"
                                    value={hookColor}
                                    onChange={(e) => setHookColor(e.target.value.toUpperCase())}
                                    placeholder="#FFFFFF"
                                    className="flex-1 border-2 border-blue-200 dark:border-blue-700 p-2 rounded font-mono text-sm uppercase"
                                    maxLength={7}
                                />
                            </div>
                        </label>

                        {/* Quick Color Presets */}
                        <div className="space-y-2">
                            <span className="text-sm font-medium">Quick Colors</span>
                            <div className="flex gap-2 flex-wrap">
                                {[
                                    { name: 'White', color: '#FFFFFF' },
                                    { name: 'Yellow', color: '#FFFF00' },
                                    { name: 'Cyan', color: '#00FFFF' },
                                    { name: 'Lime', color: '#00FF00' },
                                    { name: 'Orange', color: '#FFA500' },
                                ].map((preset) => (
                                    <button
                                        key={preset.color}
                                        type="button"
                                        onClick={() => setHookColor(preset.color)}
                                        className="w-8 h-8 rounded border-2 border-gray-300 dark:border-gray-600 hover:scale-110 transition-transform"
                                        style={{ backgroundColor: preset.color }}
                                        title={preset.name}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        {/* CTA Section */}
        <div className="p-3 bg-white dark:bg-gray-900/50 rounded-lg border-2 border-blue-200 dark:border-blue-700">
            {/* CTA Toggle */}
            <label className="flex items-center gap-3 mb-3 cursor-pointer">
                <input
                    type="checkbox"
                    checked={ctaVisible}
                    onChange={(e) => setCtaVisible(e.target.checked)}
                    className="w-4 h-4"
                />
                <div className="flex-1">
                    <div className="font-medium">📺 Show CTA Text on Screen</div>
                    <div className="text-xs text-blue-600 dark:text-blue-400">
                        Display CTA text visually (TTS always reads it aloud)
                    </div>
                </div>
            </label>

            {/* CTA Inputs */}
            <div className="space-y-3">
                    <div className="space-y-2">
                        <label className="text-sm font-medium">CTA Text</label>
                        <textarea
                            value={ctaText}
                            onChange={(e) => setCtaText(e.target.value)}
                            placeholder="Like and subscribe!&#10;For more quiz videos"
                            className="w-full border-2 border-blue-200 dark:border-blue-700 p-3 rounded-lg min-h-[80px] font-mono text-sm"
                            maxLength={200}
                        />
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-blue-600 dark:text-blue-400">
                                💡 Press Enter for new lines
                            </span>
                            <span className={`${ctaText.length > 180 ? 'text-orange-600' : 'text-gray-500'}`}>
                                {ctaText.length}/200
                            </span>
                        </div>
                    </div>

                    {/* CTA Color Picker */}
                    <div className="grid grid-cols-2 gap-3">
                        <label className="space-y-2">
                            <span className="text-sm font-medium flex items-center gap-2">
                                🎨 Text Color
                            </span>
                            <div className="flex gap-2">
                                <input
                                    type="color"
                                    value={ctaColor}
                                    onChange={(e) => setCtaColor(e.target.value)}
                                    className="w-12 h-10 rounded border-2 border-blue-200 dark:border-blue-700 cursor-pointer"
                                />
                                <input
                                    type="text"
                                    value={ctaColor}
                                    onChange={(e) => setCtaColor(e.target.value.toUpperCase())}
                                    placeholder="#00FF00"
                                    className="flex-1 border-2 border-blue-200 dark:border-blue-700 p-2 rounded font-mono text-sm uppercase"
                                    maxLength={7}
                                />
                            </div>
                        </label>

                        {/* Quick Color Presets */}
                        <div className="space-y-2">
                            <span className="text-sm font-medium">Quick Colors</span>
                            <div className="flex gap-2 flex-wrap">
                                {[
                                    { name: 'Lime', color: '#00FF00' },
                                    { name: 'White', color: '#FFFFFF' },
                                    { name: 'Yellow', color: '#FFFF00' },
                                    { name: 'Cyan', color: '#00FFFF' },
                                    { name: 'Orange', color: '#FFA500' },
                                ].map((preset) => (
                                    <button
                                        key={preset.color}
                                        type="button"
                                        onClick={() => setCtaColor(preset.color)}
                                        className="w-8 h-8 rounded border-2 border-gray-300 dark:border-gray-600 hover:scale-110 transition-transform"
                                        style={{ backgroundColor: preset.color }}
                                        title={preset.name}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        {/* Live Preview */}
        {(hookText.trim() || ctaText.trim()) && (
            <div className="p-4 bg-gray-900 rounded-lg border-2 border-blue-200 dark:border-blue-700">
                <div className="text-xs font-medium text-gray-400 mb-3">🎬 Video Preview:</div>
                <div className="space-y-4">
                    {hookText.trim() && (
                        <div className="p-3 bg-black rounded border border-gray-700">
                            <div className="text-xs text-gray-400 mb-2">Opening Hook:</div>
                            <div 
                                className="text-center font-bold text-lg whitespace-pre-line"
                                style={{ color: hookColor }}
                            >
                                {hookText}
                            </div>
                        </div>
                    )}
                    {ctaText.trim() && (
                        <div className="p-3 bg-black rounded border border-gray-700">
                            <div className="text-xs text-gray-400 mb-2">Closing CTA:</div>
                            <div 
                                className="text-center font-bold text-lg whitespace-pre-line"
                                style={{ color: ctaColor }}
                            >
                                {ctaText}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        )}
    </div>

    {/* Examples Section */}
    <details className="text-xs bg-blue-50 dark:bg-blue-900/30 p-3 rounded border border-blue-200 dark:border-blue-700">
        <summary className="cursor-pointer font-medium text-blue-800 dark:text-blue-200">
            💡 Examples & Best Practices
        </summary>
        <div className="mt-3 space-y-3 text-gray-700 dark:text-gray-300">
            <div>
                <div className="font-semibold text-blue-700 dark:text-blue-300 mb-1">Hook Examples:</div>
                <ul className="list-disc ml-5 space-y-1">
                    <li>"Think you know your anatomy?\nLet's find out!"</li>
                    <li>"Pop quiz time!\nHow well do you know pharmacology?"</li>
                    <li>"Ready for today's\nmedical challenge?"</li>
                </ul>
            </div>
            <div>
                <div className="font-semibold text-blue-700 dark:text-blue-300 mb-1">CTA Examples:</div>
                <ul className="list-disc ml-5 space-y-1">
                    <li>"Like this video\nand subscribe for daily quizzes!"</li>
                    <li>"Comment your score below\nand share with friends!"</li>
                    <li>"Visit autoquizai.com\nfor more study tools!"</li>
                </ul>
            </div>
            <div>
                <div className="font-semibold text-blue-700 dark:text-blue-300 mb-1">Color Tips:</div>
                <ul className="list-disc ml-5 space-y-1">
                    <li>White (#FFFFFF) - Classic, readable on dark backgrounds</li>
                    <li>Yellow (#FFFF00) - Attention-grabbing, high visibility</li>
                    <li>Lime (#00FF00) - Energetic, great for CTAs</li>
                    <li>Cyan (#00FFFF) - Modern, tech-feel</li>
                    <li>Use high contrast colors for better readability</li>
                </ul>
            </div>
        </div>
    </details>
</details>

                

                {/* Flashcards / Notes Editor */}
                {showFlashcardEditor && (
                    <div className="sm:col-span-2 space-y-3">
                        <div className="text-sm font-semibold flex items-center gap-2">
                            {mode === "notes" ? "📝 Custom Notes Items" : "🎴 Custom Flashcards"}
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                {completedFlashcardsCount} completed
                            </span>
                        </div>
                        <div className="space-y-3">
                            {flashcards.map((c, idx) => {
                                const complete = isCompleteFlashcard(c);
                                return (
                                    <div
                                        key={idx}
                                        className={`p-4 border-2 rounded-lg transition-all ${complete
                                            ? "bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-700"
                                            : "border-gray-200 dark:border-gray-700"
                                            }`}
                                    >
                                        <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3 flex items-center gap-2">
                                            {mode === "notes" ? "📄" : "🎴"} Item #{idx + 1}
                                            {complete && <span className="text-green-600">✅ Complete</span>}
                                        </div>

                                        <div className="grid gap-3 sm:grid-cols-2">
                                            <label className="space-y-2">
                                                <span className="text-sm font-medium">❓ Question</span>
                                                <textarea
                                                    value={c.question}
                                                    onChange={(e) => updateFlashcard(idx, { question: e.target.value })}
                                                    className="w-full border-2 p-3 rounded-lg min-h-[80px]"
                                                    placeholder="Enter the question..."
                                                />
                                            </label>
                                            <label className="space-y-2">
                                                <span className="text-sm font-medium">✅ Answer</span>
                                                <textarea
                                                    value={c.answer}
                                                    onChange={(e) => updateFlashcard(idx, { answer: e.target.value })}
                                                    className="w-full border-2 p-3 rounded-lg min-h-[80px]"
                                                    placeholder="Enter the answer..."
                                                />
                                            </label>
                                        </div>

                                        <label className="space-y-2 mt-3 block">
                                            <span className="text-sm font-medium">💡 Explanation (optional)</span>
                                            <textarea
                                                value={c.explanation ?? ""}
                                                onChange={(e) => updateFlashcard(idx, { explanation: e.target.value })}
                                                className="w-full border-2 p-3 rounded-lg min-h-[60px]"
                                                placeholder="Short explanation or additional context (optional)..."
                                            />
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* MCQ Editor */}
                {showMcqEditor && (
                    <div className="sm:col-span-2 space-y-3">
                        <div className="text-sm font-semibold">📝 Custom MCQs</div>
                        <div className="space-y-3">
                            {mcqs.map((q, idx) => {
                                const valid = isValidMcqDraft(q);
                                const opts = stripEmptyOptions(q.options);
                                return (
                                    <div
                                        key={idx}
                                        className={`p-3 border rounded-lg dark:border-gray-700 ${valid ? "bg-green-50 dark:bg-green-900/10" : ""
                                            }`}
                                    >
                                        <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                                            Question #{idx + 1} {valid ? "✅" : ""}{" "}
                                            <span className="ml-2 opacity-70">({opts.length}/4 options filled)</span>
                                        </div>

                                        <label className="space-y-1 block">
                                            <span className="text-xs font-medium">Question</span>
                                            <textarea
                                                value={q.question}
                                                onChange={(e) => updateMcq(idx, { question: e.target.value })}
                                                className="w-full border p-2 rounded min-h-[80px]"
                                                placeholder="Enter the MCQ question..."
                                            />
                                        </label>

                                        <div className="grid gap-2 sm:grid-cols-2 mt-2">
                                            {["A", "B", "C", "D"].map((label, oIdx) => (
                                                <label key={label} className="space-y-1">
                                                    <span className="text-xs font-medium">Option {label}</span>
                                                    <input
                                                        value={q.options?.[oIdx] ?? ""}
                                                        onChange={(e) => updateMcqOption(idx, oIdx, e.target.value)}
                                                        className="w-full border p-2 rounded"
                                                        placeholder={`Option ${label}...`}
                                                    />
                                                </label>
                                            ))}
                                        </div>

                                        <div className="grid gap-2 sm:grid-cols-2 mt-2">
                                            <label className="space-y-1">
                                                <span className="text-xs font-medium">Answer</span>
                                                <input
                                                    value={q.answer}
                                                    onChange={(e) => updateMcq(idx, { answer: e.target.value })}
                                                    className="w-full border p-2 rounded"
                                                    placeholder="A, B, C, D OR exact option text"
                                                />
                                            </label>
                                            <label className="space-y-1">
                                                <span className="text-xs font-medium">Explanation (optional)</span>
                                                <input
                                                    value={q.explanation ?? ""}
                                                    onChange={(e) => updateMcq(idx, { explanation: e.target.value })}
                                                    className="w-full border p-2 rounded"
                                                    placeholder="Short explanation (optional)..."
                                                />
                                            </label>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Number of Questions */}
                <label className="space-y-1">
                    <span className="text-sm font-medium">
                        📊 Number of Questions (max {MAX_Q_PER_VIDEO}){" "}
                        {mode === "notes" && <span className="ml-2 text-xs text-blue-500">(auto-set)</span>}
                    </span>
                    <input
                        type="number"
                        min={1}
                        max={MAX_Q_PER_VIDEO}
                        value={mode === "notes" ? (notesForcedNumQ ?? numQ) : numQ}
                        onChange={(e) => {
                            if (mode === "notes") return;
                            setNumQ(clampInt(Number(e.target.value), 1, MAX_Q_PER_VIDEO));
                        }}
                        className={`w-full border p-2 rounded ${mode === "notes" ? "bg-gray-100 dark:bg-gray-800" : ""}`}
                        disabled={mode === "notes"}
                    />
                    {mode === "notes" && (
                        <div className="text-xs text-blue-600 dark:text-blue-400">
                            Notes mode automatically sets this to the number of completed items above.
                        </div>
                    )}
                </label>

                {/* Seconds per Question */}
                <label className="space-y-1">
                    <span className="text-sm font-medium">⏱️ Seconds per Question</span>
                    <input
                        type="number"
                        min={4}
                        max={60}
                        value={dur}
                        onChange={(e) => setDur(Number(e.target.value))}
                        className="w-full border p-2 rounded"
                    />
                </label>
            </div>

            {/* Generate Button */}
            <div className="space-y-3">
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleGenerate}
                        disabled={generating || Boolean(quota && quota.videos.remaining === 0)}
                        className={`px-8 py-4 rounded-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed font-bold text-lg flex items-center gap-3 transition-all transform hover:scale-105 ${mode === "notes"
                            ? "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
                            : mode === "flashcards"
                                ? "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
                                : "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                            }`}
                    >
                        {generating ? (
                            <>
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" />
                                <span>Generating {mode === "notes" ? "notes study video" : mode === "quiz_reveal" ? "Quiz Reveal video" : mode === "flashcards" ? "Q&A reveal video" : "MCQ video"}...</span>
                            </>
                        ) : (
                            <>
                                <span className="text-2xl">{mode === "notes" ? "🧾" : mode === "quiz_reveal" ? "📝" : mode === "flashcards" ? "🎴" : "📝"}</span>
                                <span>
                                    Generate {mode === "notes" ? "Notes Study" : mode === "quiz_reveal" ? "Quiz Reveal" : mode === "flashcards" ? "Q&A Reveal" : "MCQ"} Video — 5 credits
                                    {mode === "notes" && " ⭐"}
                                </span>
                            </>
                        )}
                    </button>
                </div>

                <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    <kbd className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-xs font-mono">Ctrl+Enter</kbd>
                    Quick generate keyboard shortcut
                </p>
                {generating && (
                    <div className="mt-3 p-3 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 text-sm text-indigo-700 dark:text-indigo-300 flex items-start gap-2">
                        <span className="text-lg">🎬</span>
                        <div>
                            <p className="font-medium">You can leave this page and check My Videos when your video is ready.</p>
                            <p className="text-xs text-indigo-500 dark:text-indigo-400 mt-0.5">
                                Estimated time: ~{Math.ceil(estimateVideoSeconds({ numQ: (mode === "notes" ? (notesForcedNumQ ?? numQ) : numQ) || 1, secondsPerQ: dur || 6, mode }) / 60)} min — feel free to close this tab.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}








